import { Button, Spacer, Text } from '@loomhq/lens';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeModal } from 'actions/modals';
import { requestVideoPlayerCurrentTime } from 'actions/videoPlayer';
import {
  updateWelcomeComplete,
  updateWelcomeUseCasePlan,
} from 'actions/welcome';
import { CREATION_METHOD_ANON_RECORD_REPLY } from 'constants/comments';
import {
  COMPLETED_ONBOARDING_STEP,
  ONBOARDING_FLOW_COMPLETE,
} from 'constants/events';
import { ANON_RECORD_A_REPLY } from 'constants/onboarding';
import { LOOM_URI } from 'constants/routes';
import { WORK } from 'constants/useCasePlans';
import { addCommentCreator } from 'creators/commentsGraphQl';
import { patchUserPersonaInfoCreator } from 'creators/welcome';
import { selectCurrentUser } from 'selectors/user';
import * as analytics from 'utilities/analytics';
import { incrementMetric } from 'utilities/metrics';
import {
  getVideoOwnerId,
  updateAnonRecordingOwnership,
} from 'utilities/shareVideo';
import { secondsToVideoTS } from 'utilities/time';
import { getParam } from 'utilities/url';

import useLoomImg from '../assets/use_loom.svg';

import {
  MAP_CLEARBIT_TO_USE_CASE,
  MAP_USE_CASE_TO_CLEARBIT,
  PersonaPicker,
  USE_CASE_EDUCATION,
  USE_CASE_PERSONAL,
  USE_CASE_WORK,
} from 'components/welcome-webapp-simplified/steps/persona-use-case-step';

const SharePersonaUseCase = ({ currentStep }) => {
  const dispatch = useDispatch();

  const currentUseCasePlan = useSelector(state => state.welcome.use_case_plan);
  const currentCompanyRole = useSelector(state => state.welcome.role);
  const user = useSelector(selectCurrentUser);

  const isWorkPersona = currentUseCasePlan === WORK;

  const [formIsComplete, setFormIsComplete] = useState(false);

  const [useCaseSelection, setUseCaseSelection] = useState(
    MAP_CLEARBIT_TO_USE_CASE[currentUseCasePlan]
  );

  const [isLoading, setIsLoading] = useState(false);

  const videoCurrentTime = getParam('videoCurrentTime') || 0;

  useEffect(() => {
    dispatch(
      updateWelcomeUseCasePlan(MAP_USE_CASE_TO_CLEARBIT[useCaseSelection])
    );

    if ([USE_CASE_EDUCATION, USE_CASE_PERSONAL].includes(useCaseSelection)) {
      setFormIsComplete(true);
    }

    if (useCaseSelection === USE_CASE_WORK) {
      setFormIsComplete(Boolean(currentCompanyRole));
    }
  }, [dispatch, useCaseSelection, currentCompanyRole]);

  const onSubmit = async () => {
    if (!useCaseSelection) {
      return;
    }

    setIsLoading(true);

    await dispatch(
      patchUserPersonaInfoCreator({
        use_case_plan: currentUseCasePlan,
        use_case_plan_persona: useCaseSelection,
        persona_role: isWorkPersona ? currentCompanyRole : null,
        complete: true,
      })
    );

    await updateAnonRecordingOwnership();

    dispatch(
      updateWelcomeUseCasePlan(MAP_USE_CASE_TO_CLEARBIT[useCaseSelection])
    );

    dispatch(requestVideoPlayerCurrentTime(videoCurrentTime));

    const recordedReplyVideoId = getParam('replyVideoId');
    const addReplyId = getParam('addReplyId');

    const { data } = await getVideoOwnerId(recordedReplyVideoId);

    if (data.video.owner_id === user.id) {
      const comment = `${LOOM_URI}/share/${recordedReplyVideoId}`;

      dispatch(
        addCommentCreator({
          comment,
          addReplyId,
          anonUserName: null,
          creationMethod: CREATION_METHOD_ANON_RECORD_REPLY,
        })
      );
    }

    dispatch(updateWelcomeComplete(true));

    analytics.track(COMPLETED_ONBOARDING_STEP, {
      step: currentStep,
      source: ANON_RECORD_A_REPLY,
    });

    analytics.track(ONBOARDING_FLOW_COMPLETE, {
      step: currentStep,
      source: ANON_RECORD_A_REPLY,
    });

    incrementMetric('button.click', {
      context: COMPLETED_ONBOARDING_STEP,
      step: currentStep,
      source: ANON_RECORD_A_REPLY,
    });

    window.history.replaceState(null, null, window.location.pathname);

    dispatch(closeModal());
  };

  const currentTime =
    videoCurrentTime > 0
      ? ` at ${secondsToVideoTS(Math.round(videoCurrentTime))}`
      : '';

  const buttonText = `Save and comment${currentTime}`;

  return (
    <>
      <div className="text:center">
        <img src={useLoomImg} width="110" height="110" />
      </div>

      <Spacer top={2} bottom={1}>
        <Text size="xlarge" fontWeight="bold">
          What do you plan to use Loom for?
        </Text>
      </Spacer>
      <Text size="medium">
        We will provide helpful resources in your new Loom Workspace.
      </Text>

      <Spacer top={3} bottom={4}>
        <PersonaPicker
          selection={useCaseSelection}
          setSelection={setUseCaseSelection}
          currentCompanyRole={currentCompanyRole}
        />
      </Spacer>

      <div className="text:right">
        <Button
          variant="primary"
          onClick={onSubmit}
          isDisabled={!formIsComplete}
          hasLoader={isLoading}
        >
          {buttonText}
        </Button>
      </div>
    </>
  );
};

export default SharePersonaUseCase;
